/**
 * Swaps the spec values that genuinely differ between variants of one product
 * - a mouse's shell size and its weight - when the visitor picks a variant.
 *
 * WHY THIS EXISTS
 *
 * A mouse sold in Mini/Medium/Max is three different shells behind one
 * product page. Shopify stores that per variant, and the Liquid renders the
 * SELECTED variant's values on first paint. But assets/variant-picker.js
 * changes variants client-side - it dispatches `variant:changed` and rewrites
 * the URL without reloading - so without this file the numbers on screen stay
 * the first variant's for the rest of the visit, which is worse than showing
 * one size for everything: it looks specific and is wrong.
 *
 * The data comes from a JSON map the section prints, holding only variants
 * that actually carry their own metafields. On the great majority of products
 * that map is empty and this file does nothing at all.
 *
 * The millimetre-to-inch conversion is duplicated from
 * snippets/dimension-cards.liquid. That is deliberate and it is two lines: the
 * alternative is a round trip to the Section Rendering API for four numbers
 * the page already has.
 */
(function () {
  function parseSpecs(root) {
    var script = root.querySelector('[data-variant-specs]');
    if (!script) return null;
    try {
      var data = JSON.parse(script.textContent || '{}');
      return Object.keys(data).length ? data : null;
    } catch (error) {
      /* Malformed JSON must not take the variant picker down with it - the
         page is still perfectly usable showing the first variant's specs. */
      return null;
    }
  }

  function setText(el, value) {
    if (el) el.textContent = value;
  }

  function updateDimensionCards(root, dimensions) {
    var grid = root.querySelector('[data-dimension-cards]');
    if (!grid || !dimensions) return;

    var parts = String(dimensions)
      .replace(/×/g, 'x')
      .replace(/X/g, 'x')
      .replace(/mm/gi, '')
      .split('x')
      .map(function (p) { return p.trim(); })
      .filter(Boolean);

    /* Same guard as the snippet: exactly three numbers or leave the cards
       alone. A value naming two sizes at once is not a set of cards. */
    if (parts.length !== 3) return;

    parts.forEach(function (mm, index) {
      var cell = grid.querySelector('[data-dim-part="' + index + '"]');
      if (!cell) return;
      setText(cell.querySelector('[data-dim-mm]'), mm);
      var inches = Math.round((parseFloat(mm) / 25.4) * 100) / 100;
      setText(cell.querySelector('[data-dim-in]'), inches + ' in');
    });
  }

  function updateWeightCard(root, weight) {
    var cell = root.querySelector('[data-dim-part="weight"]');
    if (!cell || !weight) return;

    var bracket = String(weight).split('(');
    setText(cell.querySelector('[data-dim-weight]'), bracket[0].trim());

    var note = cell.querySelector('[data-dim-weight-note]');
    if (!note) return;
    if (bracket.length > 1) {
      var text = bracket[1].replace(')', '').trim();
      note.textContent = text.charAt(0).toUpperCase() + text.slice(1);
      note.hidden = false;
    } else {
      /* Hidden rather than emptied: an empty sub-line still occupies its
         line box and the card below it shifts. */
      note.hidden = true;
    }
  }

  function setup(root) {
    var specs = parseSpecs(root);
    if (!specs) return;

    document.addEventListener('variant:changed', function (event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;

      var values = specs[String(variant.id)];
      /* A variant with no entry keeps whatever is on screen. Blanking the
         fields would be worse: the visitor sees the size vanish and cannot
         tell whether that means "unknown" or "we broke". */
      if (!values) return;

      root.querySelectorAll('[data-vspec]').forEach(function (el) {
        var key = el.dataset.vspec;
        if (values[key]) el.textContent = values[key];
      });

      /* `dimensions` here is already RESOLVED - a pad's size and thickness were
         composed into it by the same Liquid that renders the first paint. This
         file deliberately does not know that rule: two copies of it would be
         two things to keep in step, and the copy in the language nobody edits
         is the one that would drift. */
      updateDimensionCards(root, values.dimensions);
      updateWeightCard(root, values.spec_weight);
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('.section-main-product').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();

